import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Phone, Mail, MapPin, Globe, FileText, Eye, Trash2, Check, X, Clock, LogIn, LogOut, Download, Filter, Edit, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  checked_in: 'bg-green-100 text-green-800',
  checked_out: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800'
};

const statusIcons = {
  pending: Clock,
  confirmed: Check,
  checked_in: LogIn,
  checked_out: LogOut,
  cancelled: X
};

// Function to download file using backend endpoint
const downloadFile = async (bookingId, guestName) => {
  try {
    toast.info('Downloading...');
    const token = localStorage.getItem('adminToken');
    const response = await fetch(`${BACKEND_URL}/api/admin/bookings/${bookingId}/download-id`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Download failed');
    }
    
    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `ID_${guestName.replace(/\s+/g, '_')}`;
    
    // Try to get filename from header
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) {
        filename = match[1];
      }
    }
    
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    toast.success('Download complete!');
  } catch (error) {
    console.error('Download error:', error);
    toast.error('Download failed. Please try again.');
  }
};

const AdminBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exportStatus, setExportStatus] = useState('confirmed');
  const [exportApartment, setExportApartment] = useState('all');
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchBookings();
    fetchApartments();
  }, [navigate]);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`
  });

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/bookings`, {
        headers: getAuthHeaders()
      });
      setBookings(response.data.bookings || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Failed to load bookings');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchApartments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/apartments`, {
        headers: getAuthHeaders()
      });
      setApartments(response.data.apartments || []);
    } catch (error) {
      console.error('Failed to load apartments:', error);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      
      await axios.put(
        `${BACKEND_URL}/api/admin/bookings/${bookingId}/status`,
        formData,
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Booking status updated to ${newStatus}`);
      fetchBookings();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/bookings/${bookingId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Booking deleted');
      fetchBookings();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  const viewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetailModal(true);
  };

  const openEditModal = (booking) => {
    setEditForm({
      guest_name: booking.guest_name || '',
      email: booking.email || '',
      phone: booking.phone || '',
      nationality: booking.nationality || '',
      origin: booking.origin || '',
      check_in: booking.check_in || '',
      check_out: booking.check_out || '',
      apartment_id: booking.apartment_id || '',
      apartment_name: booking.apartment_name || '',
      notes: booking.notes || ''
    });
    setSelectedBooking(booking);
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleApartmentChange = (e) => {
    const aptId = e.target.value;
    const apt = apartments.find(a => a.id === aptId);
    setEditForm(prev => ({
      ...prev,
      apartment_id: aptId,
      apartment_name: apt?.name || ''
    }));
  };

  const saveBookingEdit = async () => {
    if (!selectedBooking) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/bookings/${selectedBooking.id}`,
        editForm,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success('Booking updated successfully');
      fetchBookings();
      setShowEditModal(false);
    } catch (error) {
      toast.error('Failed to update booking');
    } finally {
      setSaving(false);
    }
  };

  // Filter bookings by date range and apartment
  const getFilteredByDateRange = (bookingsList, fromDate, toDate, status, apartmentId) => {
    return bookingsList.filter(booking => {
      // Filter by status
      if (status !== 'all' && booking.status !== status) {
        return false;
      }
      
      // Filter by apartment
      if (apartmentId !== 'all') {
        if (apartmentId === 'unassigned') {
          if (booking.apartment_id) return false;
        } else {
          if (booking.apartment_id !== apartmentId) return false;
        }
      }
      
      // Filter by date range (check-in date)
      if (fromDate && toDate) {
        const checkIn = new Date(booking.check_in);
        const from = new Date(fromDate);
        const to = new Date(toDate);
        return checkIn >= from && checkIn <= to;
      }
      
      return true;
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!dateFrom || !dateTo) {
      toast.error('Please select both start and end dates');
      return;
    }

    const filteredData = getFilteredByDateRange(bookings, dateFrom, dateTo, exportStatus, exportApartment);
    
    if (filteredData.length === 0) {
      toast.error('No bookings found for the selected criteria');
      return;
    }

    // CSV headers
    const headers = [
      'Guest Name',
      'Email',
      'Phone',
      'Nationality',
      'Origin',
      'Check-in',
      'Check-out',
      'Apartment',
      'Status',
      'Notes',
      'Booking Date'
    ];

    // CSV rows
    const rows = filteredData.map(booking => [
      booking.guest_name,
      booking.email,
      booking.phone,
      booking.nationality,
      booking.origin,
      booking.check_in,
      booking.check_out,
      booking.apartment_name || 'Any',
      booking.status,
      booking.notes || '',
      new Date(booking.created_at).toLocaleDateString()
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Generate filename
    const apartmentName = exportApartment === 'all' 
      ? 'all-apartments' 
      : exportApartment === 'unassigned' 
        ? 'unassigned' 
        : apartments.find(a => a.id === exportApartment)?.name?.replace(/\s+/g, '-') || 'apartment';
    
    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `bookings_${apartmentName}_${exportStatus}_${dateFrom}_to_${dateTo}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${filteredData.length} bookings to CSV`);
    setShowExportModal(false);
  };

  // Get count for export preview
  const getExportPreviewCount = () => {
    if (!dateFrom || !dateTo) return 0;
    return getFilteredByDateRange(bookings, dateFrom, dateTo, exportStatus, exportApartment).length;
  };

  const filteredBookings = filter === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === filter);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                size="sm"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-amber-900">Guest Bookings</h1>
                <p className="text-gray-500 text-sm">Manage guest reservations and check-ins</p>
              </div>
            </div>
            
            {/* Filter & Export */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowExportModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Download size={16} className="mr-2" />
                Export Bookings
              </Button>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="all">All Bookings ({bookings.length})</option>
                <option value="pending">Pending ({bookings.filter(b => b.status === 'pending').length})</option>
                <option value="confirmed">Confirmed ({bookings.filter(b => b.status === 'confirmed').length})</option>
                <option value="checked_in">Checked In ({bookings.filter(b => b.status === 'checked_in').length})</option>
                <option value="checked_out">Checked Out ({bookings.filter(b => b.status === 'checked_out').length})</option>
                <option value="cancelled">Cancelled ({bookings.filter(b => b.status === 'cancelled').length})</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600">No bookings found</h3>
            <p className="text-gray-400 mt-2">
              {filter === 'all' ? 'Guest bookings will appear here' : `No ${filter} bookings`}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredBookings.map((booking) => {
              const StatusIcon = statusIcons[booking.status] || Clock;
              return (
                <div 
                  key={booking.id}
                  className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Guest Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800">{booking.guest_name}</h3>
                          <p className="text-sm text-gray-500">{booking.email}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{booking.phone}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Globe className="w-4 h-4" />
                          <span>{booking.nationality}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span>{booking.origin}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(booking.check_in)} - {formatDate(booking.check_out)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Status & Actions */}
                    <div className="flex items-center gap-3">
                      {booking.id_document_url && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          ID
                        </span>
                      )}
                      <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${statusColors[booking.status]}`}>
                        <StatusIcon className="w-4 h-4" />
                        {booking.status.replace('_', ' ')}
                      </span>
                      
                      <Button
                        onClick={() => viewBookingDetails(booking)}
                        variant="outline"
                        size="sm"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-amber-600" />
              Booking Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-6">
              {/* Guest Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Guest Name</label>
                  <p className="font-medium">{selectedBooking.guest_name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Email</label>
                  <p className="font-medium">{selectedBooking.email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Phone</label>
                  <p className="font-medium">{selectedBooking.phone}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Nationality</label>
                  <p className="font-medium">{selectedBooking.nationality}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Coming From</label>
                  <p className="font-medium">{selectedBooking.origin}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Apartment</label>
                  <p className="font-medium">{selectedBooking.apartment_name || 'Any available'}</p>
                </div>
              </div>

              {/* Stay Dates */}
              <div className="bg-amber-50 rounded-lg p-4">
                <h4 className="font-medium text-amber-900 mb-2">Stay Period</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-500">Check-in</label>
                    <p className="font-medium">{formatDate(selectedBooking.check_in)}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Check-out</label>
                    <p className="font-medium">{formatDate(selectedBooking.check_out)}</p>
                  </div>
                </div>
              </div>

              {/* ID Document */}
              {selectedBooking.id_document_url && (
                <div>
                  <label className="text-sm text-gray-500 flex items-center gap-1 mb-2">
                    <FileText className="w-4 h-4" />
                    ID Document
                  </label>
                  {selectedBooking.id_document_url.endsWith('.pdf') ? (
                    <div className="flex flex-wrap gap-3">
                      <a 
                        href={selectedBooking.id_document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        <Eye className="w-5 h-5" />
                        View PDF
                      </a>
                      <button 
                        onClick={() => downloadFile(selectedBooking.id, selectedBooking.guest_name)}
                        className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 bg-green-50 hover:bg-green-100 px-4 py-2 rounded-lg font-medium transition-colors"
                      >
                        <Download className="w-5 h-5" />
                        Download PDF
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <img 
                        src={selectedBooking.id_document_url}
                        alt="ID Document"
                        className="max-w-full max-h-64 rounded-lg border shadow-sm"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <p className="text-red-500 text-sm hidden">Unable to load image. <a href={selectedBooking.id_document_url} target="_blank" rel="noopener noreferrer" className="underline">Click to open in new tab</a></p>
                      <div className="flex flex-wrap gap-3">
                        <a 
                          href={selectedBooking.id_document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View Image
                        </a>
                        <button 
                          onClick={() => downloadFile(selectedBooking.id, selectedBooking.guest_name)}
                          className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 bg-green-50 hover:bg-green-100 px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download ID
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {selectedBooking.notes && (
                <div>
                  <label className="text-sm text-gray-500">Additional Notes</label>
                  <p className="font-medium bg-gray-50 p-3 rounded-lg">{selectedBooking.notes}</p>
                </div>
              )}

              {/* Status Update */}
              <div className="border-t pt-4">
                <label className="text-sm text-gray-500 mb-2 block">Update Status</label>
                <div className="flex flex-wrap gap-2">
                  {['pending', 'confirmed', 'checked_in', 'checked_out', 'cancelled'].map(status => {
                    const StatusIcon = statusIcons[status];
                    return (
                      <Button
                        key={status}
                        onClick={() => updateStatus(selectedBooking.id, status)}
                        variant={selectedBooking.status === status ? 'default' : 'outline'}
                        size="sm"
                        className={selectedBooking.status === status ? 'bg-amber-600' : ''}
                      >
                        <StatusIcon className="w-4 h-4 mr-1" />
                        {status.replace('_', ' ')}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between border-t pt-4">
                <div className="flex gap-2">
                  <Button
                    onClick={() => openEditModal(selectedBooking)}
                    variant="outline"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit Booking
                  </Button>
                  <Button
                    onClick={() => deleteBooking(selectedBooking.id)}
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
                <Button onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Booking Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Booking
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Guest Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name</label>
                <Input
                  name="guest_name"
                  value={editForm.guest_name || ''}
                  onChange={handleEditChange}
                  placeholder="Guest name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <Input
                  name="email"
                  type="email"
                  value={editForm.email || ''}
                  onChange={handleEditChange}
                  placeholder="Email"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <Input
                  name="phone"
                  value={editForm.phone || ''}
                  onChange={handleEditChange}
                  placeholder="Phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nationality</label>
                <Input
                  name="nationality"
                  value={editForm.nationality || ''}
                  onChange={handleEditChange}
                  placeholder="Nationality"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Coming From</label>
              <Input
                name="origin"
                value={editForm.origin || ''}
                onChange={handleEditChange}
                placeholder="City, Country"
              />
            </div>

            {/* Stay Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date</label>
                <Input
                  name="check_in"
                  type="date"
                  value={editForm.check_in || ''}
                  onChange={handleEditChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date</label>
                <Input
                  name="check_out"
                  type="date"
                  value={editForm.check_out || ''}
                  onChange={handleEditChange}
                />
              </div>
            </div>

            {/* Apartment Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Apartment</label>
              <select
                value={editForm.apartment_id || ''}
                onChange={handleApartmentChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Any available / Not assigned</option>
                {apartments.map(apt => (
                  <option key={apt.id} value={apt.id}>{apt.name} - {apt.type}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={editForm.notes || ''}
                onChange={handleEditChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional notes..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                onClick={() => setShowEditModal(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={saveBookingEdit}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-green-600" />
              Export Bookings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a date range and status to export bookings to CSV file.
            </p>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Booking Status
              </label>
              <select
                value={exportStatus}
                onChange={(e) => setExportStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Statuses</option>
                <option value="confirmed">Confirmed</option>
                <option value="checked_in">Checked In</option>
                <option value="checked_out">Checked Out</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Apartment Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apartment
              </label>
              <select
                value={exportApartment}
                onChange={(e) => setExportApartment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Apartments</option>
                <option value="unassigned">Unassigned / Any Available</option>
                {apartments.map(apt => (
                  <option key={apt.id} value={apt.id}>{apt.name} - {apt.type}</option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Date (Check-in)
                </label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To Date (Check-in)
                </label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  min={dateFrom}
                  className="w-full"
                />
              </div>
            </div>

            {/* Preview Count */}
            {dateFrom && dateTo && (
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-sm text-gray-600">
                  <span className="font-bold text-2xl text-green-600">{getExportPreviewCount()}</span>
                  <br />
                  bookings match your criteria
                </p>
              </div>
            )}

            {/* Export Button */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowExportModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={exportToCSV}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                disabled={!dateFrom || !dateTo || getExportPreviewCount() === 0}
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBookings;
